import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toAbsoluteUrl } from "../../../_metronic/helpers";
import { useUserAuth } from "../../../context/AuthContext";
import { usePortal } from "../../../context/PortalContext";
import { Notice } from "../../common/cards/Notice";
import { AuthHelpers } from "./AuthHelpers";
import { EmailSentNotice } from "./EmailSentNotice";
import { useLogger } from "../../../context/LoggerContext";
import { validateEmail } from "../../../helpers/ValidationHelpers";
import { buildDraftKey, getDraftUserId, readSessionDraft } from "../../../helpers/SessionStorageHelpers";
import { useGlobalLoader } from "../../../context/GlobalLoaderContext";
import { trackBehaviourEvent } from "../../../helpers/userBehaviourTrackingHelpers";

type RegistrationValues = {
  firstname: string;
  lastname: string;
  email: string;
  acceptTerms: boolean;
  regSrc: string;
};

type RegistrationErrors = Partial<Record<keyof RegistrationValues, string>>;
type RegistrationTouched = Partial<Record<keyof RegistrationValues, boolean>>;

const normalizeEmail = (email?: string | null) => AuthHelpers.normalizeEmail(email);

const getSafeRedirectPath = () => {
  const rawRedirect = new URLSearchParams(window.location.search).get("redirect");

  if (!rawRedirect || rawRedirect.startsWith("//") || /^https?:\/\//i.test(rawRedirect)) {
    return "/dashboard";
  }

  return rawRedirect.startsWith("/") ? rawRedirect : `/${rawRedirect}`;
};

function getUrlRegistrationParams() {
  const urlParams = new URLSearchParams(window.location.search);
  let urlFirstName = "";
  let urlLastName = "";
  let urlEmail = "";
  let urlRegistrationSource = "";

  urlParams.forEach((value, key) => {
    const lowerKey = key.toLowerCase();
    if (lowerKey === "firstname") urlFirstName = value;
    if (lowerKey === "lastname") urlLastName = value;
    if (lowerKey === "email") urlEmail = value;
    if (lowerKey === "regsrc") urlRegistrationSource = value;
  });

  return {
    urlFirstName,
    urlLastName,
    urlEmail,
    urlRegistrationSource,
  };
}

export function Registration() {
  const { user, anonymousUser, loginWithGoogle }: any = useUserAuth();
  const {
    anonymousClientData,
    clientData,
    setClientData,
    insertClientData,
    returnClientData,
    apiUpdateClientData,
  }: any = usePortal();
  const navigate = useNavigate();
  const { showGlobalLoader } = useGlobalLoader();
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [sentEmail, setSentEmail] = useState("");
  const [accountExisted, setAccountExisted] = useState(false);
  const [existingAccountLinkSent, setExistingAccountLinkSent] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [touched, setTouched] = useState<RegistrationTouched>({});
  const [hasInitializedForm, setHasInitializedForm] = useState(false);
  const paramsProcessed = useRef(false);
  const anonymousDataFetched = useRef(false);
  const registrationViewTracked = useRef(false);
  const { logAppInsightsError } = useLogger();
  const isAnonymousUpgrade = Boolean(anonymousUser);
  const redirectPath = getSafeRedirectPath();
  const { urlFirstName, urlLastName, urlEmail, urlRegistrationSource } = useMemo(
    getUrlRegistrationParams,
    []
  );

  // Sends only for a visit that started on a tracked lander in this page load (e.g. vlm3's booking confirmation START).
  useEffect(() => {
    if (registrationViewTracked.current) return;
    registrationViewTracked.current = true;
    trackBehaviourEvent("Registration Viewed");
  }, []);

  const sessionDraftData = useMemo(
    () => readSessionDraft(buildDraftKey(getDraftUserId(user)))?.data ?? null,
    [user]
  );

  const [values, setValues] = useState<RegistrationValues>(() => ({
    firstname: urlFirstName || sessionDraftData?.firstName || "",
    lastname: urlLastName || sessionDraftData?.lastName || "",
    email: urlEmail || sessionDraftData?.email || "",
    acceptTerms: true,
    regSrc: urlRegistrationSource || "Portal",
  }));

  useEffect(() => {
    if (paramsProcessed.current) {
      return;
    }
    const urlParams = new URLSearchParams(window.location.search);
    const paramsString = urlParams.toString();
    if (!paramsString) {
      paramsProcessed.current = true;
      return;
    }

    const updatedClientData: any = { ...(clientData || {}) };
    let hasUpdates = false;

    urlParams.forEach((value, key) => {
      const parts = key.split(".");
      if (parts.length > 1) {
        let cur: any = updatedClientData;
        for (let i = 0; i < parts.length - 1; i++) {
          const p = parts[i];
          if (cur[p] == null || typeof cur[p] !== "object") {
            cur[p] = {};
          }
          cur = cur[p];
        }
        const last = parts[parts.length - 1];
        cur[last] = value;
        hasUpdates = true;
      } else {
        updatedClientData[key] = value;
        hasUpdates = true;
      }
    });

    if (hasUpdates) {
      setClientData(updatedClientData);
    }
    paramsProcessed.current = true;
  }, [clientData, setClientData]);

  useEffect(() => {
    if (
      anonymousDataFetched.current ||
      !isAnonymousUpgrade ||
      anonymousClientData?.email ||
      !returnClientData
    ) {
      return;
    }

    anonymousDataFetched.current = true;
    returnClientData();
  }, [anonymousClientData?.email, isAnonymousUpgrade, returnClientData]);

  useEffect(() => {
    const nextValues = {
      firstname:
        urlFirstName || anonymousClientData?.firstName || clientData?.firstName || sessionDraftData?.firstName || "",
      lastname:
        urlLastName || anonymousClientData?.lastName || clientData?.lastName || sessionDraftData?.lastName || "",
      email: urlEmail || anonymousClientData?.email || clientData?.email || sessionDraftData?.email || "",
      acceptTerms: true,
      regSrc: urlRegistrationSource || "Portal",
    };

    if (!hasInitializedForm) {
      setValues(nextValues);
      setHasInitializedForm(true);
      return;
    }

    if (!values.email && nextValues.email) {
      setValues((current) => ({
        ...current,
        email: nextValues.email,
      }));
    }
  }, [
    anonymousClientData?.email,
    anonymousClientData?.firstName,
    anonymousClientData?.lastName,
    clientData?.email,
    clientData?.firstName,
    clientData?.lastName,
    hasInitializedForm,
    sessionDraftData?.email,
    sessionDraftData?.firstName,
    sessionDraftData?.lastName,
    urlEmail,
    urlFirstName,
    urlLastName,
    urlRegistrationSource,
    values.email,
  ]);

  const validateValues = (candidateValues: RegistrationValues): RegistrationErrors => {
    const errors: RegistrationErrors = {};
    const email = normalizeEmail(candidateValues.email);

    if (!isAnonymousUpgrade && candidateValues.firstname.trim().length < 3) {
      errors.firstname = "First name is required";
    }

    if (!isAnonymousUpgrade && candidateValues.lastname.trim().length < 3) {
      errors.lastname = "Last name is required";
    }

    if (!email || !validateEmail(email)) {
      errors.email = "Please enter a valid email address.";
    }

    return errors;
  };

  const errors = validateValues(values);
  const isValid = Object.keys(errors).length === 0;
  const showError = (field: keyof RegistrationValues) => touched[field] && errors[field];
  const inputsDisabled = loading || accountExisted;

  const updateField = (field: keyof RegistrationValues, value: string | boolean) => {
    setValues((current) => ({
      ...current,
      [field]: value,
    }));
    setStatus(null);
    if (accountExisted) {
      setAccountExisted(false);
      setExistingAccountLinkSent(false);
      setSentEmail("");
    }
  };

  const markTouched = (field: keyof RegistrationValues) => {
    setTouched((current) => ({
      ...current,
      [field]: true,
    }));
  };

  const getRegistrationData = (email: string) => ({
    ...(clientData || {}),
    ...(anonymousClientData || {}),
    firstName: values.firstname.trim(),
    lastName: values.lastname.trim(),
    email,
    regSrc: values.regSrc,
  });

  const persistAnonymousEmailIfChanged = async (email: string) => {
    if (!isAnonymousUpgrade || !apiUpdateClientData) {
      return true;
    }

    if (!anonymousClientData?.email && returnClientData) {
      await returnClientData();
    }

    const currentAnonymousEmail = normalizeEmail(anonymousClientData?.email);

    if (currentAnonymousEmail && currentAnonymousEmail === email) {
      return true;
    }

    const responseCode = await apiUpdateClientData(getRegistrationData(email));
    return responseCode === 200;
  };

  const showExistingAccount = (email: string) => {
    trackBehaviourEvent("Existing Account Found");
    setSentEmail(email);
    setAccountExisted(true);
    setExistingAccountLinkSent(false);
  };

  const resetExistingAccountState = () => {
    setAccountExisted(false);
    setExistingAccountLinkSent(false);
    setSentEmail("");
    setStatus(null);
    setValues((current) => ({
      ...current,
      email: "",
    }));
    setTouched({});
  };

  const sendExistingAccountLink = async (email: string) => {
    setLoading(true);

    try {
      await AuthHelpers.sendSignInLink(email);
      // The lander flow ends here: finishing sign-in from the email is a new page load.
      trackBehaviourEvent("Sign In Link Sent", { page: "registration" });
      setSentEmail(email);
      setExistingAccountLinkSent(true);
      setStatus(null);
    } catch (error: any) {
      setStatus("We could not send the sign-in link. Please try again.");
      logAppInsightsError(error, "Register Existing Account Sign-In Link");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setTouched({
      firstname: true,
      lastname: true,
      email: true,
    });

    const submittedEmail = normalizeEmail(values.email);
    const nextErrors = validateValues(values);

    if (Object.keys(nextErrors).length > 0) {
      setStatus("Please check the highlighted fields.");
      return;
    }

    if (accountExisted) {
      await sendExistingAccountLink(submittedEmail);
      return;
    }

    setLoading(true);

    try {
      if (isAnonymousUpgrade) {
        const persisted = await persistAnonymousEmailIfChanged(submittedEmail);
        if (!persisted) {
          setStatus("We could not update your email. Please try again.");
          return;
        }

        const exists = await AuthHelpers.checkAccountExists(submittedEmail);
        if (exists) {
          showExistingAccount(submittedEmail);
          return;
        }

        // Creates the full account, carries the anonymous session's client data over to it, and
        // emails a sign-in link for later - all synchronously, in this tab. The user is already
        // signed in as the new account once this resolves, so we go straight to the portal
        // rather than showing a "check your email" screen.
        await AuthHelpers.createFullAccountFromAnonymous(submittedEmail);
        trackBehaviourEvent("Account Created");
        await returnClientData?.();
        // This component is about to unmount as part of the route change - its own button
        // loader unmounts with it, so hand off to the global overlay for the gap until the
        // destination page has something to show.
        showGlobalLoader("Setting up your account...");
        navigate(redirectPath, { replace: true });
        return;
      }

      const existed = await AuthHelpers.handleRegistration(
        getRegistrationData(submittedEmail),
        insertClientData,
        navigate
      );

      if (existed) {
        showExistingAccount(submittedEmail);
        return;
      }

      trackBehaviourEvent("Account Created");
      setSentEmail(submittedEmail);
      setEmailSent(true);
      setAccountExisted(false);
    } catch (error: any) {
      if (isAnonymousUpgrade && (error?.code === "auth/email-already-in-use" || error?.code === "auth/credential-already-in-use")) {
        // Someone else registered this email between our checkAccountExists call above and now.
        showExistingAccount(submittedEmail);
      } else {
        setStatus("Failed to create account. Please try again.");
        logAppInsightsError(error, "Register");
      }
    } finally {
      setLoading(false);
    }
  };

  // Google is offered only once we know the account exists: it signs in to that account, alongside the sign-in link.
  const handleGoogleSignIn = async (event: React.MouseEvent) => {
    event.preventDefault();
    setLoading(true);
    try {
      // loginWithGoogle handles its own errors and navigation internally - just mirror its
      // in-flight state on the button, same as every other submit path on this page.
      if (await loginWithGoogle(event)) trackBehaviourEvent("Signed In", { method: "google", page: "registration" });
    } finally {
      setLoading(false);
    }
  };

  if (emailSent) {
    return accountExisted ? null : (
      <EmailSentNotice email={sentEmail} />
    );
  }

  return (
    <>
      <div className="card card-rounded">
        <div className="card-body">
        <form
          className="form w-100 fv-plugins-bootstrap5 fv-plugins-framework"
          noValidate
          id="kt_login_signup_form"
          onSubmit={handleSubmit}
        >
          <div className="text-center mb-11">
            <div className="text-dark fs-1 fw-bolder mb-3">
              {isAnonymousUpgrade ? "Create your account" : "Sign Up"}
            </div>

            {isAnonymousUpgrade ? (
              <p className="text-gray-700 fw-semibold fs-5 mb-0">
                Secure your account to save quotes and unlock better rates.
              </p>
            ) : (
              <Notice
                noticeClasses="notice d-flex bg-light-warning rounded border-warning border-dashed p-6 mt-10"
                button={null}
                header={null}
                content="Get more annuity income by registering for free and telling us a few more details."
                icon={null}
                iconClasses={null}
              />
            )}
          </div>

          {status && (
            <div className="mb-lg-15 alert alert-danger">
              <div className="alert-text font-weight-bold">{status}</div>
            </div>
          )}

          {/* Once the link is sent, the confirmation replaces the "we found an account" prompt. */}
          {accountExisted && existingAccountLinkSent && (
            <div className="mb-lg-15 alert alert-success">
              <div className="alert-text font-weight-bold">
                We sent a secure sign-in link to {sentEmail || normalizeEmail(values.email)}.
              </div>
            </div>
          )}

          {accountExisted && !existingAccountLinkSent && (
            <div className="mb-lg-15 alert alert-info">
              <div className="alert-text font-weight-bold">
                We found an account for {sentEmail || normalizeEmail(values.email)}.
                {" "}Send a secure sign-in link to continue with this account.
              </div>
            </div>
          )}

          {!isAnonymousUpgrade && (
            <>
              <div className="fv-row mb-8">
                <label className="form-label fw-bolder text-dark fs-6">
                  First name
                </label>
                <input
                  placeholder="First name"
                  type="text"
                  autoComplete="given-name"
                  value={values.firstname}
                  onChange={(event) => updateField("firstname", event.target.value)}
                  onBlur={() => markTouched("firstname")}
                  disabled={inputsDisabled}
                  className={`form-control bg-transparent ${showError("firstname") ? "is-invalid" : touched.firstname ? "is-valid" : ""}`}
                />
                {showError("firstname") && (
                  <div className="fv-plugins-message-container">
                    <div className="fv-help-block">
                      <span role="alert">{errors.firstname}</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="fv-row mb-8">
                <label className="form-label fw-bolder text-dark fs-6">
                  Last name
                </label>
                <input
                  placeholder="Last name"
                  type="text"
                  autoComplete="family-name"
                  value={values.lastname}
                  onChange={(event) => updateField("lastname", event.target.value)}
                  onBlur={() => markTouched("lastname")}
                  disabled={inputsDisabled}
                  className={`form-control bg-transparent ${showError("lastname") ? "is-invalid" : touched.lastname ? "is-valid" : ""}`}
                />
                {showError("lastname") && (
                  <div className="fv-plugins-message-container">
                    <div className="fv-help-block">
                      <span role="alert">{errors.lastname}</span>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          <div className="fv-row mb-8">
            <label className="form-label fw-bolder text-dark fs-6">Email</label>
            <input
              placeholder="Email"
              type="email"
              autoComplete="email"
              value={values.email}
              onChange={(event) => updateField("email", event.target.value)}
              onBlur={() => markTouched("email")}
              disabled={inputsDisabled}
              className={`form-control bg-transparent ${showError("email") ? "is-invalid" : touched.email ? "is-valid" : ""}`}
            />
            {showError("email") && (
              <div className="fv-plugins-message-container">
                <div className="fv-help-block">
                  <span role="alert">{errors.email}</span>
                </div>
              </div>
            )}
          </div>

          <div className="text-center">
            <button
              type="submit"
              id="kt_sign_up_submit"
              className="btn btn-lg btn-primary w-100 mb-5"
              disabled={loading || (!accountExisted && !isValid)}
            >
              {!loading && (
                <span className="indicator-label">
                  {accountExisted
                    ? existingAccountLinkSent
                      ? "Send sign-in link again"
                      : "Sign in with this account"
                    : isAnonymousUpgrade
                      ? "Create my account"
                      : "Create Account"}
                </span>
              )}
              {loading && (
                <span className="indicator-progress" style={{ display: "block" }}>
                  {accountExisted
                    ? "Sending sign-in link..."
                    : isAnonymousUpgrade
                      ? "Creating your account..."
                      : "Please wait..."}{" "}
                  <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                </span>
              )}
            </button>
          </div>

          {accountExisted && (
            <>
              <div className="d-flex align-items-center gap-4 mb-5" aria-hidden="true">
                <span className="border-top flex-grow-1"></span>
                <span className="text-gray-500 fw-semibold text-uppercase fs-7">or</span>
                <span className="border-top flex-grow-1"></span>
              </div>

              <div className="row g-3 mb-5">
                <div className="col-md-12">
                  <button
                    type="button"
                    onClick={handleGoogleSignIn}
                    className="btn btn-flex btn-outline btn-text-gray-700 btn-active-color-primary bg-state-light flex-center text-nowrap w-100"
                    disabled={loading}
                  >
                    <img
                      alt="Logo"
                      src={toAbsoluteUrl("/media/svg/brand-logos/google-icon.svg")}
                      className="h-15px me-3"
                    />
                    Continue with Google
                  </button>
                </div>
              </div>

              <div className="text-center mb-5">
                <button type="button" className="btn btn-light" onClick={resetExistingAccountState} disabled={loading}>
                  Use a different email
                </button>
              </div>
            </>
          )}

          <div className="text-gray-500 text-center fw-semibold fs-6">
            Already have an account?{" "}
            <Link to="/auth/login" className="link-primary">
              Log in
            </Link>
          </div>
        </form>
        </div>
      </div>
    </>
  );
}
